import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, Info, HelpCircle } from 'lucide-react';

const FAQs = () => {
  // Color palette from your existing app
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333'
  };

  // State for expanded FAQ items and search
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Toggle FAQ item expansion
  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // FAQ categories
  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'campaign', name: 'About Our Campaign' },
    { id: 'reform', name: 'Electoral Reform' },
    { id: 'involvement', name: 'Getting Involved' },
    { id: 'strategy', name: 'Strategy & Approach' }
  ];

  // FAQ data with categories
  const faqData = [
    {
      id: 'what-is-vdm',
      category: 'campaign',
      question: 'What is Voters Deserve More?',
      answer: `Voters Deserve More is a grassroots campaign dedicated to transforming American democracy through electoral reform. We advocate for proportional representation and other changes to create a truly multi-party democracy where every voter's voice counts and all communities receive fair representation.`
    },
    {
      id: 'why-electoral-reform',
      category: 'reform',
      question: 'Why do we need electoral reform?',
      answer: `Our current electoral system wastes millions of votes, limits voter choice to just two major parties, enables gerrymandering, and produces governments that don't reflect the will of the majority. These problems lead to political polarization, voter apathy, and policies that don't represent what most Americans want. Electoral reform is necessary to create a democracy that truly represents all Americans.`
    },
    {
      id: 'what-is-proportional',
      category: 'reform',
      question: 'What is proportional representation?',
      answer: `Proportional representation is an electoral system where parties or groups win seats in proportion to the number of votes they receive. For example, if a party wins 30% of the vote, they would receive approximately 30% of the seats in the legislature. This is different from our current winner-takes-all system where 51% of votes can win 100% of representation. Proportional systems ensure that nearly all voters help elect someone who shares their values.`
    },
    {
      id: 'other-countries',
      category: 'reform',
      question: 'Do other countries use proportional representation?',
      answer: `Yes, most established democracies around the world use some form of proportional representation, including most European countries, New Zealand, Australia (Senate), and many others. The United States is one of the few major democracies that still uses a winner-takes-all system exclusively. Countries with proportional systems typically have higher voter turnout, greater citizen satisfaction with democracy, and more diverse representation.`
    },
    {
      id: 'benefits-voters',
      category: 'reform',
      question: 'How would proportional representation benefit me as a voter?',
      answer: `As a voter, proportional representation would give you more meaningful choices at the ballot box, ensure your vote isn't "wasted," and increase the likelihood that you'll have representatives who share your values. You'd no longer have to choose between "the lesser of two evils." Studies show that proportional systems lead to higher voter satisfaction and policies that better align with what the majority of citizens actually want.`
    },
    {
      id: 'how-implemented',
      category: 'reform',
      question: 'How could proportional representation be implemented in the US?',
      answer: `The most practical path would be through the Fair Representation Act at the federal level, which would create multi-member congressional districts with ranked-choice voting. States can also implement proportional systems for their state legislatures through state-level legislation or ballot initiatives. At the local level, many cities already have the authority to change their election methods and could adopt proportional systems through local ordinances or charter amendments.`
    },
    {
      id: 'multi-member',
      category: 'reform',
      question: 'What are multi-member districts?',
      answer: `Multi-member districts are electoral districts that elect multiple representatives rather than just one. For example, instead of having five single-member districts each electing one representative, you might have one larger district electing five representatives proportionally. This approach eliminates gerrymandering, ensures more diverse representation, and means fewer votes are "wasted." Most proportional systems use multi-member districts.`
    },
    {
      id: 'ranked-choice',
      category: 'reform',
      question: 'What is ranked-choice voting?',
      answer: `Ranked-choice voting allows voters to rank candidates in order of preference (1st, 2nd, 3rd, etc.). If your first-choice candidate can't win or has more votes than needed, your vote isn't wasted—it transfers to your next choice. This system eliminates the "spoiler effect," reduces negative campaigning, and ensures winners have broader support. It's already used in several U.S. cities and states including Maine, Alaska, and New York City.`
    },
    {
      id: 'different-systems',
      category: 'reform',
      question: 'What specific voting system does VDM advocate for?',
      answer: `We advocate for a variant of open-list proportional representation for most legislative bodies, similar to systems used in Germany. This system would give voters the power to choose both their preferred party and their preferred candidates within that party. For smaller states and/or municipalities, we support single transferable vote (STV), which is used in many cities around the world. Both systems ensure proportional outcomes while giving voters meaningful choices.`
    },
    {
      id: 'third-parties',
      category: 'reform',
      question: 'Would these reforms help third parties?',
      answer: `Yes, these reforms would remove the structural barriers that currently prevent third parties from winning fair representation. In proportional systems, parties typically need around 10% of the vote to win seats, compared to the near-impossible task of any third party winning 51% of votes in our current system. This would allow multiple parties across the political spectrum to win representation based on their actual level of voter support.`
    },
    {
      id: 'polarization',
      category: 'reform',
      question: 'Could electoral reform reduce political polarization?',
      answer: `Yes, when multiple parties can win seats, politicians have incentives to collaborate and find common ground rather than appeal only to their base. The presence of bridge-building parties can facilitate compromise. Additionally, when all voters have representation they support, there's less of the "my team versus your team" animosity that fuels our current polarization and turns it into "my team AND your team".`
    },
    {
      id: 'campaign-finance',
      category: 'reform',
      question: 'What does campaign finance reform have to do with proportional representation?',
      answer: `While proportional representation addresses how votes translate to seats, campaign finance reform ensures that elections aren't dominated or decided by corporations and foreign governments. Together, these reforms create a democracy where all citizens have an equal voice. Public financing of campaigns would make elected officials accountable to voters rather than donors, while proportional representation would ensure those officials actually represent the diversity of voter opinion.`
    },
    {
      id: 'get-involved',
      category: 'involvement',
      question: 'How can I get involved with Voters Deserve More?',
      answer: `There are many ways to get involved! You can sign up on our website to join our mailing list, volunteer for our campaign, donate to support our work, contact your representatives about electoral reform legislation, start or join a local chapter in your community, or simply help spread the word about proportional representation. Visit our "Get Involved" page to learn more about specific opportunities to contribute.`
    },
    {
      id: 'local-reforms',
      category: 'involvement',
      question: 'Can I push for these reforms in my local community?',
      answer: `Absolutely! Local reform efforts are a crucial part of building momentum for broader change. Many cities and counties have the authority to change their electoral systems through charter amendments or local ordinances. We can help connect you with resources and experts to explore what's possible in your community. Local successes demonstrate the benefits of reform and build support for state and national changes.`
    },
    {
      id: 'timeline',
      category: 'reform',
      question: 'Is electoral reform realistic in the near future?',
      answer: `Electoral reform is increasingly realistic as more Americans recognize the flaws in our current system and become more disenfranchised by the two party system. Ranked-choice voting has already been adopted in several cities and states, and multi-member districts with proportional representation are gaining support. Reform will likely happen incrementally, starting at local levels and building momentum. The growing dissatisfaction with our political system creates an opportunity for significant change in the coming years.`
    }
  ];

  // Filter FAQs based on search term and category
  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div style={{ backgroundColor: colorPalette.light }}>
      {/* Hero Section */}
      <section style={{
        backgroundColor: colorPalette.dark,
        color: 'white',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ 
            fontSize: '3rem',
            marginBottom: '16px',
            fontWeight: '800'
          }}>Frequently Asked Questions</h1>
          <p style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 24px',
            lineHeight: '1.6',
            opacity: '0.9'
          }}>
            Find answers to common questions about Voters Deserve More and our campaign for electoral reform.
          </p>
        </div>
      </section>

      {/* FAQ Search and Categories */}
      <section style={{
        padding: '40px 0 20px',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            backgroundColor: colorPalette.light,
            borderRadius: '8px',
            padding: '25px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              position: 'relative',
              marginBottom: '25px'
            }}>
              <input
                type="text"
                placeholder="Search frequently asked questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 15px',
                  fontSize: '1rem',
                  border: `1px solid ${colorPalette.tertiary}`,
                  borderRadius: '6px',
                  backgroundColor: 'white'
                }}
              />
              <Search 
                size={20} 
                color={colorPalette.dark} 
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} 
              />
            </div>
            
            <div style={{
              display: 'flex',
              overflowX: 'auto',
              gap: '10px',
              paddingBottom: '10px'
            }}>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  style={{
                    padding: '8px 15px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: activeCategory === category.id ? 
                      colorPalette.primary : 'white',
                    color: activeCategory === category.id ? 
                      'white' : colorPalette.dark,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: activeCategory === category.id ? '600' : '400',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Listings */}
      <section style={{
        padding: '20px 0 60px',
        backgroundColor: 'white'
      }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map(faq => (
              <div 
                key={faq.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  border: `1px solid ${colorPalette.tertiary}`,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div 
                  onClick={() => toggleItem(faq.id)}
                  style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: expandedItems[faq.id] ? 
                      `${colorPalette.primary}10` : 'white',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <HelpCircle 
                      size={20} 
                      color={colorPalette.primary} 
                      style={{ flexShrink: 0 }} 
                    />
                    <h3 style={{ 
                      margin: 0, 
                      fontWeight: '600',
                      fontSize: '1.1rem',
                      color: expandedItems[faq.id] ? 
                        colorPalette.primary : colorPalette.dark,
                    }}>
                      {faq.question}
                    </h3>
                  </div>
                  {expandedItems[faq.id] ? 
                    <ChevronUp size={20} color={colorPalette.primary} /> : 
                    <ChevronDown size={20} color={colorPalette.dark} />}
                </div>
                
                {expandedItems[faq.id] && (
                  <div style={{
                    padding: '20px 20px 20px 55px',
                    borderTop: `1px solid ${colorPalette.tertiary}`,
                    backgroundColor: `${colorPalette.primary}05`,
                  }}>
                    <p style={{ 
                      lineHeight: '1.6',
                      fontSize: '1rem',
                      color: colorPalette.dark
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{
              padding: '30px',
              textAlign: 'center',
              backgroundColor: colorPalette.light,
              borderRadius: '8px',
              marginBottom: '30px'
            }}>
              <Info size={30} color={colorPalette.primary} style={{ marginBottom: '15px' }} />
              <h3 style={{ marginBottom: '10px', color: colorPalette.dark }}>No matching questions found</h3>
              <p style={{ marginBottom: '20px' }}>
                We couldn't find an answer to your question. Feel free to ask us directly!
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                flexWrap: 'wrap'
              }}>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('all');
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: colorPalette.secondary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Reset search
                </button>
                <Link 
                  to="/contact"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: colorPalette.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                >
                  Contact us directly
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: colorPalette.tertiary
      }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ 
            fontSize: '2rem',
            marginBottom: '20px',
            color: colorPalette.dark
          }}>
            Still Have Questions?
          </h2>
          <p style={{ 
            fontSize: '1.1rem',
            marginBottom: '30px',
            color: colorPalette.dark,
            maxWidth: '700px',
            margin: '0 auto 30px'
          }}>
            If you couldn't find the answer you were looking for, feel free to reach out to us directly.
            Our team is here to help.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <Link to="/contact" style={{
              backgroundColor: colorPalette.primary,
              color: 'white',
              padding: '12px 25px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Contact Us
            </Link>
            <Link to="/get-involved" style={{
              backgroundColor: 'white',
              color: colorPalette.primary,
              padding: '12px 25px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              border: `1px solid ${colorPalette.primary}`
            }}>
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQs;